import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import BaseDialog from '../../../components/shared/dialogs/BaseDialog'
import DialogFooter from '../../../components/shared/dialogs/DialogFooter'
import FormErrorSummary from '../../../components/admin/shared/FormErrorSummary'
import {
  AdminFormSection,
  FieldGroupHeading,
} from '../../../components/admin/shared/fields'
import { useNotify } from '../../../components/shared/ToastProvider'
import { COMPACT_TEXTFIELD_SX } from '../../../theme/textFieldStyles'

import { adminFeedbackApi, type AdminFeedbackUpdate } from '../../../services/feedbackApi'
import { generateFullUrl } from '../../../utils/urls'
import {
  FEEDBACK_IMPACT_OPTIONS,
  FEEDBACK_STATUS_META,
  FEEDBACK_STATUS_OPTIONS,
  FEEDBACK_TYPE_OPTIONS,
  type AdminFeedbackDetail,
  type FeedbackStatus,
} from '../../../types/feedback'
import { formatRelativeTimestamp } from '../../../utils/dateFormatters'

interface FeedbackDetailDialogProps {
  feedbackId: number | null
  open: boolean
  onClose: () => void
  onUpdated?: (row: AdminFeedbackDetail) => void
}

function labelFor(value: string, options: { value: string; label: string }[]): string {
  return options.find(o => o.value === value)?.label || value
}

/** Coloured dot + label, shared by the Select's menu items and its value. */
const StatusOption: React.FC<{ value: FeedbackStatus }> = ({ value }) => {
  const meta = FEEDBACK_STATUS_META[value]
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          bgcolor: meta.color,
          flexShrink: 0,
        }}
      />
      <span>{meta.label}</span>
    </Stack>
  )
}

const FeedbackDetailDialog: React.FC<FeedbackDetailDialogProps> = ({
  feedbackId,
  open,
  onClose,
  onUpdated,
}) => {
  const { showSuccess } = useNotify()
  const [detail, setDetail] = useState<AdminFeedbackDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [statusValue, setStatusValue] = useState<FeedbackStatus>('new')
  const [updateNote, setUpdateNote] = useState('')
  const [internalNote, setInternalNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [showContext, setShowContext] = useState(false)

  useEffect(() => {
    if (!open || !feedbackId) {
      setDetail(null)
      setError(null)
      setShowContext(false)
      return
    }
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const data = await adminFeedbackApi.get(feedbackId)
        if (cancelled) return
        setDetail(data)
        setStatusValue(data.status)
        setUpdateNote(data.update_note || '')
        setInternalNote(data.internal_note || '')
      } catch (err: any) {
        if (!cancelled) setError('Failed to load feedback')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [open, feedbackId])

  // Dirty across status + both notes. Notes compare against '' when unset.
  const dirty =
    !!detail &&
    (statusValue !== detail.status ||
      updateNote !== (detail.update_note ?? '') ||
      internalNote !== (detail.internal_note ?? ''))

  const handleSave = async () => {
    if (!detail || !dirty) return
    const payload: AdminFeedbackUpdate = {}
    if (statusValue !== detail.status) payload.status = statusValue
    if (updateNote !== (detail.update_note ?? '')) {
      payload.update_note = updateNote.trim() ? updateNote.trim() : null
    }
    if (internalNote !== (detail.internal_note ?? '')) {
      payload.internal_note = internalNote.trim() ? internalNote.trim() : null
    }

    setSaving(true)
    setError(null)
    try {
      const updated = await adminFeedbackApi.update(detail.id, payload)
      showSuccess('Feedback updated')
      onUpdated?.(updated)
      onClose()
    } catch (err: any) {
      const msg = err?.response?.data?.detail || 'Failed to update feedback'
      setError(typeof msg === 'string' ? msg : 'Failed to update feedback')
    } finally {
      setSaving(false)
    }
  }

  // Short heading ("View Feedback #7") with the feedback title carried in the
  // subtitle line beneath it, rather than one long, truncated heading.
  const feedbackTitle = detail?.title?.trim()
  const dialogTitle = detail
    ? `View Feedback #${detail.id}`
    : feedbackId
      ? `View Feedback #${feedbackId}`
      : 'View Feedback'

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={dialogTitle}
      subtitle={feedbackTitle}
      variant="info"
      size="md"
      footer={
        <DialogFooter
          cancel={{ label: 'Close', onClick: onClose, disabled: saving }}
          primary={{
            label: saving ? 'Saving...' : 'Save',
            onClick: handleSave,
            pending: saving,
            disabled: !dirty,
          }}
        />
      }
    >
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      <FormErrorSummary message={error} />

      {!loading && detail && (
        <Grid container spacing={2}>
          {/* LEFT column (2/3): the report narrative */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              <AdminFormSection
                card
                title="Reporter"
                action={
                  detail.country ? (
                    <Chip size="small" label={detail.country} variant="outlined" />
                  ) : undefined
                }
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {detail.user_name || detail.user_email || `User #${detail.user_id}`}
                </Typography>
                {detail.user_email && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {detail.user_email}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {detail.fp_code || 'No FP'}
                  {detail.fp_name ? ` · ${detail.fp_name}` : ''}
                  {' · '}
                  {(() => {
                    const ts = formatRelativeTimestamp(detail.created_at)
                    return (
                      <time dateTime={ts.dateTime} title={ts.title}>
                        {ts.label}
                      </time>
                    )
                  })()}
                </Typography>
              </AdminFormSection>

              <AdminFormSection
                card
                title="User Report"
                action={
                  <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                    <Chip
                      size="small"
                      label={labelFor(detail.type, FEEDBACK_TYPE_OPTIONS)}
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      label={labelFor(detail.impact, FEEDBACK_IMPACT_OPTIONS)}
                      variant="outlined"
                    />
                  </Stack>
                }
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {detail.title || <em>(derived from details)</em>}
                </Typography>

                <FieldGroupHeading>What&apos;s happening?</FieldGroupHeading>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {detail.details}
                </Typography>

                {detail.trying_to_do && (
                  <>
                    <FieldGroupHeading>Trying to do</FieldGroupHeading>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {detail.trying_to_do}
                    </Typography>
                  </>
                )}

                <FieldGroupHeading>Follow up via email</FieldGroupHeading>
                <Typography variant="body2">{detail.follow_up_ok ? 'Yes' : 'No'}</Typography>
              </AdminFormSection>

              {/* Diagnostics sit under the report, in the left column. */}
              {detail.context && (
                <AdminFormSection card title="Application & user details">
                  {detail.context.route?.pathname &&
                    (() => {
                      const route = detail.context!.route
                      const fullUrl = generateFullUrl(route.pathname + (route.search || ''))
                      const referrer = route.referrer || null
                      return (
                        <Box sx={{ mb: 1 }}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{ flexWrap: 'wrap', gap: 0.5 }}
                          >
                            <Button
                              size="small"
                              variant="outlined"
                              endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                              href={fullUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Open page
                            </Button>
                            {referrer && (
                              <Typography variant="caption" color="text.secondary">
                                Came from{' '}
                                <Link
                                  href={generateFullUrl(referrer)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  underline="hover"
                                >
                                  {referrer}
                                </Link>
                              </Typography>
                            )}
                          </Stack>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: 'block',
                              mt: 0.5,
                              fontFamily: 'monospace',
                              wordBreak: 'break-all',
                            }}
                          >
                            {route.pathname}
                            {route.search || ''}
                          </Typography>
                          {detail.context!.entry_referrer && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', wordBreak: 'break-all' }}
                            >
                              Entry: {detail.context!.entry_referrer}
                            </Typography>
                          )}
                        </Box>
                      )
                    })()}

                  <FieldGroupHeading underCardTitle={!detail.context.route?.pathname}>
                    Environment
                  </FieldGroupHeading>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {[
                      detail.browser_family && detail.context.browser?.os
                        ? `${detail.browser_family} on ${detail.context.browser.os}`
                        : detail.browser_family,
                      detail.app_sha && detail.app_sha !== 'unknown' && !/^0+(\.0+)*$/.test(detail.app_sha)
                        ? `build ${detail.app_sha.slice(0, 7)}`
                        : null,
                      detail.context.client_ip ? `ip ${detail.context.client_ip}` : null,
                      detail.context.correlation_id
                        ? `req ${detail.context.correlation_id.slice(0, 8)}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography>

                  <Box sx={{ mt: 1 }}>
                    <Link
                      component="button"
                      type="button"
                      variant="caption"
                      underline="hover"
                      onClick={() => setShowContext(v => !v)}
                    >
                      {showContext ? 'Hide full context JSON' : 'View full context JSON'}
                      {showContext ? (
                        <ExpandLessIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }} />
                      ) : (
                        <ExpandMoreIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }} />
                      )}
                    </Link>
                  </Box>
                  {showContext && (
                    <Box
                      component="pre"
                      sx={{
                        mt: 1,
                        p: 1.5,
                        bgcolor: 'grey.100',
                        borderRadius: 1,
                        fontSize: 11,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                        maxHeight: 320,
                        overflowY: 'auto',
                      }}
                    >
                      {JSON.stringify(detail.context, null, 2)}
                    </Box>
                  )}
                </AdminFormSection>
              )}
            </Grid>
          </Grid>

          {/* RIGHT column (1/3): triage actions */}
          <Grid item xs={12} md={4}>
            <Grid container spacing={2}>
              {/* Single-control card: the card heading is the label, so the
                  Select carries none of its own. Coloured dot per status. */}
              <AdminFormSection card title="Status">
                <FormControl fullWidth size="small">
                  <Select
                    value={statusValue}
                    onChange={e => setStatusValue(e.target.value as FeedbackStatus)}
                    renderValue={val => <StatusOption value={val as FeedbackStatus} />}
                  >
                    {FEEDBACK_STATUS_OPTIONS.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        <StatusOption value={opt.value} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </AdminFormSection>

              {/* Triage notes. Card title is the label (matching the Status
                  card); the bare multiline carries no floating label. */}
              <AdminFormSection card title="Update for submitter">
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  size="small"
                  placeholder="Shared with the person who sent this feedback"
                  helperText="Visible to the submitter in their My feedback list - they're notified when you save it."
                  value={updateNote}
                  onChange={e => setUpdateNote(e.target.value)}
                  inputProps={{ maxLength: 2000 }}
                  sx={COMPACT_TEXTFIELD_SX}
                />
              </AdminFormSection>

              <AdminFormSection card title="Internal note">
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  size="small"
                  placeholder="Team only - not shown to the submitter"
                  helperText="Private to the Evo team."
                  value={internalNote}
                  onChange={e => setInternalNote(e.target.value)}
                  inputProps={{ maxLength: 2000 }}
                  sx={COMPACT_TEXTFIELD_SX}
                />
              </AdminFormSection>
            </Grid>
          </Grid>
        </Grid>
      )}
    </BaseDialog>
  )
}

export default FeedbackDetailDialog
